import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';

interface FlyingPinwheel {
  id: number;
  size: number;
  startX: number;
  startY: number;
  topY: number;
  rightX: number;
  bottomY: number;
  leftX: number;
}

type SeparationDirection = 'horizontal' | 'cardinal' | 'perimeter';

@Component({
  selector: 'app-brand-loading-mask',
  templateUrl: './brand-loading-mask.component.html',
  styleUrls: ['./brand-loading-mask.component.css']
})
export class BrandLoadingMaskComponent implements OnDestroy {
  @ViewChild('pinwheel', { static: true })
  pinwheel: ElementRef<HTMLImageElement>;

  private isSeparated = false;
  private nextFlyingPinwheelId = 0;
  private launchTimer: ReturnType<typeof setTimeout> = null;

  @Input()
  set separateForDialog(value: boolean) {
    this.isSeparated = !!value;
    if (!this.isSeparated) {
      this.flyingPinwheels = [];
    }
  }

  get separateForDialog(): boolean {
    return this.isSeparated;
  }

  private readonly separationDirections: SeparationDirection[] = [
    'horizontal',
    'cardinal',
    'perimeter'
  ];

  separationDirection: SeparationDirection = this.separationDirections[
    Math.floor(Math.random() * this.separationDirections.length)
  ];
  flyingPinwheels: FlyingPinwheel[] = [];

  onPinwheelCycleStart(event: AnimationEvent): void {
    if (event.animationName !== 'brand-pinwheel-cycle') {
      return;
    }

    this.scheduleFlyingPinwheel();
  }

  onPinwheelCycle(event: AnimationEvent): void {
    if (event.animationName !== 'brand-pinwheel-cycle') {
      return;
    }

    this.scheduleFlyingPinwheel();
  }

  private scheduleFlyingPinwheel(): void {
    if (this.launchTimer) {
      clearTimeout(this.launchTimer);
    }

    this.launchTimer = setTimeout(() => {
      this.launchTimer = null;
      this.launchFlyingPinwheel();
    }, 1900);
  }

  private launchFlyingPinwheel(): void {
    if (!this.separateForDialog || this.separationDirection !== 'perimeter' || this.prefersReducedMotion()) {
      return;
    }

    const rect = this.pinwheel.nativeElement.getBoundingClientRect();
    const size = window.innerWidth <= 760 ? 36 : 44;
    const edge = 12;
    const startX = rect.left + (rect.width - size) / 2;
    const startY = rect.top + (rect.height - size) / 2;

    const flyingPinwheel: FlyingPinwheel = {
      id: ++this.nextFlyingPinwheelId,
      size,
      startX,
      startY,
      topY: edge,
      rightX: window.innerWidth - size - edge,
      bottomY: window.innerHeight - size - edge,
      leftX: edge
    };

    this.flyingPinwheels = [
      ...this.flyingPinwheels,
      flyingPinwheel
    ];

    setTimeout(() => this.startFlyingPinwheel(flyingPinwheel));
  }

  private startFlyingPinwheel(pinwheel: FlyingPinwheel): void {
    const element = document.querySelector(
      `[data-flying-pinwheel-id="${pinwheel.id}"]`
    ) as HTMLElement;

    if (!element) {
      return;
    }

    const distanceUp = Math.abs(pinwheel.startY - pinwheel.topY);
    const distanceRight = Math.abs(pinwheel.rightX - pinwheel.startX);
    const distanceDown = Math.abs(pinwheel.bottomY - pinwheel.topY);
    const distanceLeft = Math.abs(pinwheel.rightX - pinwheel.leftX);
    const totalDistance = distanceUp + distanceRight + distanceDown + distanceLeft;
    const duration = totalDistance / 300 * 1000;
    const topOffset = distanceUp / totalDistance;
    const rightOffset = (distanceUp + distanceRight) / totalDistance;
    const bottomOffset = (distanceUp + distanceRight + distanceDown) / totalDistance;
    const fadeInOffset = Math.min(0.02, topOffset / 2);

    const animation = element.animate([
      { top: `${pinwheel.startY}px`, left: `${pinwheel.startX}px`, opacity: 0 },
      { top: `${pinwheel.startY}px`, left: `${pinwheel.startX}px`, opacity: 1, offset: fadeInOffset },
      { top: `${pinwheel.topY}px`, left: `${pinwheel.startX}px`, opacity: 1, offset: topOffset },
      { top: `${pinwheel.topY}px`, left: `${pinwheel.rightX}px`, opacity: 1, offset: rightOffset },
      { top: `${pinwheel.bottomY}px`, left: `${pinwheel.rightX}px`, opacity: 1, offset: bottomOffset },
      { top: `${pinwheel.bottomY}px`, left: `${pinwheel.leftX}px`, opacity: 1, offset: 0.98 },
      { top: `${pinwheel.bottomY}px`, left: `${pinwheel.leftX}px`, opacity: 0 }
    ], {
      duration,
      easing: 'linear',
      fill: 'forwards'
    });

    animation.onfinish = () => this.removeFlyingPinwheel(pinwheel.id);
  }

  removeFlyingPinwheel(id: number): void {
    this.flyingPinwheels = this.flyingPinwheels.filter(pinwheel => pinwheel.id !== id);
  }

  trackFlyingPinwheel(index: number, pinwheel: FlyingPinwheel): number {
    return pinwheel.id;
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  ngOnDestroy(): void {
    if (this.launchTimer) {
      clearTimeout(this.launchTimer);
    }
  }
}
